import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { IResponse } from '@shared/models/base/http-response.model';

@Injectable({
  providedIn: 'root'
})
export class FileDownloadService {

  // DI services
  private readonly http: HttpClient = inject(HttpClient);

  // Service actions
  async downloadFile(endpoint: string, suggestedFileName = 'download', postParams: unknown = null): Promise<void> {
    const blobResponse = await this.getFileData(endpoint, postParams);

    if (!blobResponse.data)
      throw new Error('Failed to download file');

    this.downloadBlob(blobResponse.data, suggestedFileName, blobResponse.message);
  }

  downloadBlob(blob: Blob, suggestedFileName = 'download', contentDisposition = ''): void {
    const filename = this.getFileName(contentDisposition, suggestedFileName);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async getFileData(endpoint: string, postParams: unknown = null): Promise<IResponse<Blob>> {
    const url = new URL(endpoint, environment.apiUrl).href;
    const request$ = postParams !== null
      ? this.http.post(url, postParams, {
        observe: 'response',
        responseType: 'blob',
      })
      : this.http.get(url, {
        observe: 'response',
        responseType: 'blob',
      });
    const response = await firstValueFrom(request$);
    const contentType = response.headers.get('Content-Type') ?? '';

    if (!response.body) {
      throw new Error('Failed to download file');
    }

    if (contentType.toLowerCase().includes('application/json')) {
      const json = await this.parseJsonResponse(response.body);

      if (!json.success) {
        throw new Error(json.message ?? 'Failed to download file');
      }

      throw new Error('Unexpected response from JSON');
    }

    return {
      success: true,
      message: response.headers.get('Content-Disposition') ?? '',
      data: new Blob([response.body], { type: contentType || response.body.type }),
    };
  }

  private async parseJsonResponse(blob: Blob): Promise<IResponse<unknown>> {
    try {
      return JSON.parse(await blob.text()) as IResponse<unknown>;
    } catch {
      throw new Error('Failed to download file');
    }
  }

  private getFileName(contentDisposition: string, suggestedFileName: string): string {
    const match = contentDisposition.match(/filename="?([^";]+)"?/);
    return match?.[1] ?? suggestedFileName;
  }

}
